import React from 'react';
import { Spinner } from '../../components/ui';
import ExportButton from '../../components/ExportButton';

// Desktop Brief Report: the four headline numbers, then one block per part of
// the brief. Each block's heading carries its checkbox - untick it and it
// leaves the screen and the exported file together. Logic: useBrief.
export default function BriefDesktop({ vm }) {
  const {
    canView, loading, blocks, toggleBlock, blockLabels, twoSided,
    functions, pastFunctions, fnOff, toggleFunction, shownFunctions,
    memberRows, memberTotals,
    invitedByRows, invitedByTotals,
    cardStats,
    moneyRows, moneyTotals, catOff, toggleCategory, withEstimates, setWithEstimates,
    summary, report, money, reload,
  } = vm;

  if (!canView) {
    return (
      <div>
        <div className="page-head"><h1>Brief Report</h1></div>
        <div className="card card-pad empty">The brief covers the whole event, so only an Event Admin can open it.</div>
      </div>
    );
  }

  const Head = ({ k, extra }) => (
    <div className="brief-head">
      <label className="brief-tick">
        <input type="checkbox" checked={blocks[k]} onChange={() => toggleBlock(k)} />
        <strong>{blockLabels[k]}</strong>
      </label>
      {extra}
    </div>
  );

  return (
    <div className="brief-page">
      <div className="page-head">
        <h1>Brief Report</h1>
        <div className="spacer" />
        <button className="btn light sm" onClick={reload}>↻ Refresh</button>
        <ExportButton report={report} label="⇪ Export brief" className="btn primary" />
      </div>

      <div className="mn-tiles">
        {summary.map(s => (
          <div key={s.label} className="mn-tile"><span>{s.label}</span><strong>{s.value}</strong></div>
        ))}
      </div>

      {loading ? <div className="card card-pad"><Spinner /></div> : (
        <>
          {/* 1 — persons per function */}
          <div className="card brief-block">
            <Head k="members" extra={<span className="muted">
              Tick the functions the brief should cover
              {pastFunctions.length > 0 && ` · ${pastFunctions.map(f => f.FunctionName).join(', ')} ${pastFunctions.length === 1 ? 'is' : 'are'} over and left out`}
            </span>} />
            {blocks.members && (
              <table className="tbl compact">
                <thead>
                  <tr>
                    <th style={{ width: 28 }} />
                    <th className="grow">Function</th><th>Date</th><th>Time</th>
                    <th className="mn-r">Parties</th><th className="mn-r">Named</th><th className="mn-r">Extra card</th>
                    <th className="mn-r">Total persons</th>
                    {twoSided && <><th className="mn-r">Groom</th><th className="mn-r">Bride</th></>}
                  </tr>
                </thead>
                <tbody>
                  {memberRows.map(r => (
                    <tr key={r.key} className={r.on ? '' : 'brief-off'}>
                      <td><input type="checkbox" checked={r.on} onChange={() => toggleFunction(r.FunctionId)} /></td>
                      <td>{r.FunctionName}</td><td>{r.date}</td><td>{r.time || '—'}</td>
                      <td className="mn-r">{r.parties}</td><td className="mn-r">{r.named}</td><td className="mn-r">{r.extra || ''}</td>
                      <td className="mn-r"><strong>{r.persons}</strong></td>
                      {twoSided && <><td className="mn-r">{r.groom || ''}</td><td className="mn-r">{r.bride || ''}</td></>}
                    </tr>
                  ))}
                  {memberRows.length === 0 && <tr><td colSpan={twoSided ? 10 : 8} className="empty">No functions yet.</td></tr>}
                </tbody>
                <tfoot>
                  <tr>
                    <td /><td colSpan={3}><strong>Total</strong></td>
                    <td className="mn-r">{memberTotals.parties}</td><td className="mn-r">{memberTotals.named}</td>
                    <td className="mn-r">{memberTotals.extra || ''}</td>
                    <td className="mn-r"><strong>{memberTotals.persons}</strong></td>
                    {twoSided && <><td className="mn-r">{memberTotals.groom || ''}</td><td className="mn-r">{memberTotals.bride || ''}</td></>}
                  </tr>
                </tfoot>
              </table>
            )}
          </div>

          {/* 2 — who invited how many */}
          <div className="card brief-block">
            <Head k="invitedBy" extra={<span className="muted">{invitedByRows.length} {invitedByRows.length === 1 ? 'person' : 'people'} have invited</span>} />
            {blocks.invitedBy && (
              <div className="table-wrap">
                <table className="tbl compact">
                  <thead>
                    <tr>
                      <th className="grow">Invited by</th><th>Mobile</th>
                      <th className="mn-r">Guests</th><th className="mn-r">Persons</th><th className="mn-r">Cards</th>
                      {shownFunctions.map(f => <th key={f.FunctionId} className="mn-r">{f.FunctionName}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {invitedByRows.map(r => (
                      <tr key={r.key}>
                        <td>{r.name}{r.isGuest && <span className="chip" style={{ marginLeft: 6 }}>Guest</span>}</td>
                        <td>{r.mobile || '—'}</td>
                        <td className="mn-r">{r.guests}</td><td className="mn-r"><strong>{r.persons}</strong></td>
                        <td className="mn-r">{r.totalCards}</td>
                        {shownFunctions.map(f => <td key={f.FunctionId} className="mn-r">{r.perFn[String(f.FunctionId)] || ''}</td>)}
                      </tr>
                    ))}
                    {invitedByRows.length === 0 && <tr><td colSpan={5 + shownFunctions.length} className="empty">No guests yet.</td></tr>}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2}><strong>Total</strong></td>
                      <td className="mn-r">{invitedByTotals.guests}</td>
                      <td className="mn-r"><strong>{invitedByTotals.persons}</strong></td>
                      <td className="mn-r">{invitedByTotals.totalCards}</td>
                      {shownFunctions.map(f => (
                        <td key={f.FunctionId} className="mn-r">
                          {invitedByRows.reduce((s, r) => s + (r.perFn[String(f.FunctionId)] || 0), 0) || ''}
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* 3 — cards to order */}
          <div className="card brief-block">
            <Head k="cards" extra={<span className="muted">{cardStats.households} households · {cardStats.toOrder} to order</span>} />
            {blocks.cards && (
              <>
                <table className="tbl compact">
                  <thead>
                    <tr>
                      <th className="grow">Card / insert</th><th>Kind</th><th>Given for</th>
                      <th className="mn-r">Households</th><th className="mn-r">Extra cards</th><th className="mn-r">To order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cardStats.rows.map(r => (
                      <tr key={r.key}>
                        <td>{r.name}</td><td>{r.kind}</td><td className="muted">{r.forFunctions || '—'}</td>
                        <td className="mn-r">{r.households}</td><td className="mn-r">{r.extra || ''}</td>
                        <td className="mn-r"><strong>{r.total}</strong></td>
                      </tr>
                    ))}
                    {cardStats.rows.length === 0 && (
                      <tr><td colSpan={6} className="empty">No card types yet — set them up on the Cards page.</td></tr>
                    )}
                  </tbody>
                  {cardStats.rows.length > 0 && (
                    <tfoot>
                      <tr>
                        <td colSpan={3}><strong>Total to order</strong></td>
                        <td className="mn-r">{cardStats.rows.reduce((s, r) => s + r.households, 0)}</td>
                        <td className="mn-r">{cardStats.rows.reduce((s, r) => s + r.extra, 0) || ''}</td>
                        <td className="mn-r"><strong>{cardStats.toOrder}</strong></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
                <div className="muted brief-note">
                  {cardStats.extraCards ? `${cardStats.extraCards} extra cards (${cardStats.extraPersons} persons) · ` : ''}
                  {cardStats.ownFamily ? `${cardStats.ownFamily} own family — no card · ` : ''}
                  {cardStats.withoutType ? `${cardStats.withoutType} households not covered by any card type` : ''}
                  {!cardStats.extraCards && !cardStats.ownFamily && !cardStats.withoutType ? 'Every household is covered by a card type.' : ''}
                </div>
              </>
            )}
          </div>

          {/* 4 — money */}
          <div className="card brief-block">
            <Head k="money" extra={
              <label className="brief-tick">
                <input type="checkbox" checked={withEstimates} onChange={e => setWithEstimates(e.target.checked)} />
                Count amounts still marked “estimate”
              </label>
            } />
            {blocks.money && (
              <>
                <table className="tbl compact">
                  <thead>
                    <tr>
                      <th style={{ width: 28 }} />
                      <th className="grow">Category</th><th className="mn-r">Items</th>
                      <th className="mn-r">Amount</th><th className="mn-r">Paid in advance</th>
                      <th className="mn-r">Balance to pay</th><th className="mn-r">Estimates</th>
                    </tr>
                  </thead>
                  <tbody>
                    {moneyRows.map(r => (
                      <tr key={r.key} className={r.on ? '' : 'brief-off'}>
                        <td><input type="checkbox" checked={r.on} onChange={() => toggleCategory(r.key)} /></td>
                        <td>{r.category}{r.fromServices ? <span className="muted" style={{ fontSize: 11.5 }}> · from Services</span> : null}</td>
                        <td className="mn-r">{r.items}</td>
                        <td className="mn-r">{money(r.total)}</td>
                        <td className="mn-r">{money(r.paid)}</td>
                        <td className="mn-r"><strong>{money(r.balance)}</strong></td>
                        <td className="mn-r">{r.estimates || ''}</td>
                      </tr>
                    ))}
                    {moneyRows.length === 0 && <tr><td colSpan={7} className="empty">Nothing booked yet.</td></tr>}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td /><td><strong>Total{moneyTotals.estimates ? ' (incl. estimates)' : ''}</strong></td>
                      <td />
                      <td className="mn-r"><strong>{money(moneyTotals.total)}</strong></td>
                      <td className="mn-r"><strong>{money(moneyTotals.paid)}</strong></td>
                      <td className="mn-r"><strong>{money(moneyTotals.balance)}</strong></td>
                      <td className="mn-r">{moneyTotals.estimates || ''}</td>
                    </tr>
                  </tfoot>
                </table>
                <div className="muted brief-note">
                  Untick a category and it leaves the totals and the printed sheet — that is how the brief says
                  which amounts are needed and which are not. Per-person amounts follow the headcount above,
                  so they move when guests are added.
                </div>
              </>
            )}
          </div>

          {fnOff.size > 0 && (
            <div className="muted brief-note">
              {functions.length - fnOff.size} of {functions.length} functions are in this brief.
            </div>
          )}
          {catOff.size > 0 && <div className="muted brief-note">{catOff.size} cost {catOff.size === 1 ? 'category' : 'categories'} left out.</div>}
        </>
      )}
    </div>
  );
}
